import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import cloneDeep from 'lodash/cloneDeep';
import {
  addFlowAITasks,
  deleteTasks,
  fetchChildrenTasks,
  fetchTasks,
  insertRecipe,
  splitLocationsIntoTasks,
  updateTasks,
  uploadTasks,
} from '../../tasks/store/tasksSlice';
import {
  AddDependenciesAPI,
  BulkDeleteDependencyAPI,
  BulkUpdateDependencyAPI,
  BulkUploadDependencyAPI,
  SyncDependenciesAPI,
  UpdateDependencyAPI,
} from '../api/DependencyService';

/**
 * Create dependency
 * @param {*} data
 * @param {Number} data.id - successor id
 * @param {String} data.inputString - input string i.e H223FS + 2d
 * @param {Object} data.parsed - From predecessor dialog
 */
export const createDependencyFromString = createAsyncThunk(
  'dependencies/createDependencyFromString',
  async (data, { rejectWithValue, getState }) => {
    const taskState = getState().tasks;
    const dependenciesState = getState().dependencies;
    const dependencies = [];
    if (data?.inputString) {
      //Split on ','
      const dependencyStrings = data.inputString.split(',');
      //For each string
      for (let string of dependencyStrings) {
        let dependency = {};
        string = string.trim();
        let uuid = string.match(/^[a-zA-Z0-9]*/i);
        dependency.pred_task_id = taskState.ids.find(
          (taskId) => taskState.entities[taskId].uuid === uuid[0]
        );
        //Get type
        let type = string.match(/fs|sf|ss|ff/i);
        dependency.type = type ? type[0].toUpperCase() : 'FS';
        //Get lag
        let lagString = string.match(/(\+|-)\s*\d+/i);
        if (lagString) {
          dependency.lag = parseInt(lagString[0].replace(' ', ''));
        } else {
          dependency.lag = 0;
        }
        //Check if exists
        const update = dependenciesState.ids.find((depId) => {
          return (
            dependenciesState.entities[depId].succ_task_id === data.id &&
            dependenciesState.entities[depId].pred_task_id === dependency.pred_task_id
          );
        });
        if (update) {
          dependency.id = update;
        }
        if (dependency.pred_task_id) {
          dependencies.push(dependency);
        }
      }
    }
    const apiResponse = await SyncDependenciesAPI({ id: data.id, dependencies: dependencies });
    if (apiResponse?.error) {
      return rejectWithValue(apiResponse.response.error);
    } else {
      return apiResponse.response.data;
    }
  }
);

/**
 * Add dependency
 * @param {object} data
 * @param {object} data.parameters
 */
export const addDependency = createAsyncThunk(
  'dependencies/addDependency',
  async (data, { rejectWithValue }) => {
    const apiResponse = await AddDependenciesAPI(data);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response;
    }
  }
);

/**
 * Update dependency
 * @param {object} data
 * @param {object} data.parameters
 */
export const updateDependency = createAsyncThunk(
  'dependencies/updateDependency',
  async (data, { rejectWithValue }) => {
    const apiResponse = await UpdateDependencyAPI(data);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response;
    }
  }
);

/**
 * Sync dependency
 * @param {object} requestParameters
 * @param {string} requestParameters.taskId
 * @param {object} requestParameters.selectedDependency
 */
export const syncDependency = createAsyncThunk(
  'dependencies/syncDependency',
  async (requestParameters, { rejectWithValue }) => {
    try {
      const dependencyList = [];
      const dependencyWithIds = [];
      const removeIds = [];

      requestParameters.selectedDependency?.forEach((obj) => {
        const cloneObj = cloneDeep(obj);
        if (cloneObj.isNew) {
          removeIds.push(obj.id);
          delete cloneObj['id'];
        }
        if (cloneObj.source && cloneObj.target) {
          dependencyList.push(cloneObj);
          dependencyWithIds.push(obj);
        }
      });
      const apiResponse = await SyncDependenciesAPI({
        id: requestParameters.taskId,
        dependencies: dependencyList,
      });

      if (apiResponse.error || apiResponse.response.errors) {
        return rejectWithValue(
          apiResponse.error
            ? apiResponse.error
            : { errors: apiResponse.response.errors, dependencyWithIds }
        );
      } else {
        return { ...apiResponse.response.data, removeIds };
      }
    } catch (error) {
      console.error('syncDependency error =>', error);
    }
  }
);

/**
 * Bulk Update dependency
 * @param {object} data
 * @param {object} data.parameters
 */
export const updateDependencies = createAsyncThunk(
  'dependencies/updateDependencies',
  async (data, { rejectWithValue }) => {
    const apiResponse = await BulkUpdateDependencyAPI(data);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response;
    }
  }
);

/**
 * Bulk Delete dependency
 * @param {object} data
 * @param {object} data.parameters
 */
export const bulkDeleteDependency = createAsyncThunk(
  'dependencies/bulkDeleteDependency',
  async (data, { rejectWithValue, getState }) => {
    const apiResponse = await BulkDeleteDependencyAPI(data);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      const dependencies = cloneDeep(getState().dependencies.entities);
      const taskData = data.ids.map((id) => {
        return { taskId: dependencies[id]?.target, dependencyId: id };
      });
      return { ids: data.ids, taskData };
    }
  }
);

/**
 * Bulk Upload dependency
 * @param {object} data
 * @param {object} data.parameters
 */
export const bulkUploadDependency = createAsyncThunk(
  'dependencies/bulkUploadDependency',
  async (data, { rejectWithValue, getState }) => {
    const apiResponse = await BulkUploadDependencyAPI(data);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response.data;
    }
  }
);

export const dependenciesAdapter = createEntityAdapter({});

const initialState = dependenciesAdapter.getInitialState({
  loading: false,
});

const dependenciesSlice = createSlice({
  name: 'dependencies',
  initialState,
  reducers: {
    saveDependency: (state, action) => {
      dependenciesAdapter.upsertOne(state, action.payload);
    },
    deleteDependency: (state, action) => {
      dependenciesAdapter.removeOne(state, action.payload.id);
    },
    clearDependencies: (state, action) => {
      dependenciesAdapter.removeAll(state);
    },
    updateDependencyId: (state, action) => {
      dependenciesAdapter.upsertOne(state, action.payload);
      dependenciesAdapter.removeOne(state, action.payload.tempId);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTasks.fulfilled, (state, action) => {
      if (action.payload?.entities?.dependencies) {
        dependenciesAdapter.setAll(state, action.payload?.entities?.dependencies);
      } else {
        dependenciesAdapter.setAll(state, []);
      }
    });

    // Create Dependency
    builder.addCase(createDependencyFromString.fulfilled, (state, action) => {
      if (action.payload?.created_dependencies) {
        dependenciesAdapter.upsertMany(state, action.payload.created_dependencies);
      }
      if (action.payload?.updated_dependencies) {
        dependenciesAdapter.upsertMany(state, action.payload.updated_dependencies);
      }
      if (action.payload?.deleted_dependencies) {
        dependenciesAdapter.removeMany(
          state,
          action.payload.deleted_dependencies.map((dep) => dep.id)
        );
      }
      if (action.payload?.affected_tasks) {
        dependenciesAdapter.updateMany(state, action.payload.affected_tasks);
      }
    });
    builder.addCase(createDependencyFromString.rejected, (state, action) => {
      state.error = action.payload.error;
    });

    // Add Dependency
    builder.addCase(addDependency.fulfilled, (state, action) => {
      dependenciesAdapter.upsertOne(state, action.payload?.data);
    });

    // Update Dependency
    builder.addCase(updateDependency.fulfilled, (state, action) => {
      dependenciesAdapter.upsertOne(state, action.payload);
    });

    // Sync Dependency
    builder.addCase(syncDependency.fulfilled, (state, action) => {
      if (action.payload?.created_dependencies) {
        dependenciesAdapter.upsertMany(state, action.payload.created_dependencies);
      }
      if (action.payload?.deleted_dependencies) {
        dependenciesAdapter.removeMany(
          state,
          action.payload.deleted_dependencies.map((dep) => dep.id)
        );
      }
      if (action.payload?.updated_dependencies) {
        dependenciesAdapter.upsertMany(state, action.payload.updated_dependencies);
      }
      if (action.payload?.removeIds) {
        dependenciesAdapter.removeMany(state, action.payload?.removeIds);
      }
    });

    builder.addCase(syncDependency.rejected, (state, action) => {
      const errorDependencyList = [];
      if (action.payload.errors) {
        const errorKeys = Object.keys(action.payload.errors);
        errorKeys.forEach((key) => {
          errorDependencyList.push({
            ...action.payload.dependencyWithIds[key],
            error: action.payload.errors[key],
          });
        });
      }
      if (errorDependencyList && errorDependencyList.length) {
        dependenciesAdapter.upsertMany(
          state,
          errorDependencyList?.map((obj) => {
            return { id: obj.id, error: obj.error };
          })
        );
      }
    });

    //Delete Task
    builder.addCase(deleteTasks.fulfilled, (state, action) => {
      if (action.payload?.removedDependencies) {
        dependenciesAdapter.removeMany(
          state,
          action.payload.removedDependencies.map((dependency) => dependency.id)
        );
      }
    });

    // Fill Tasks with recipes
    builder.addCase(insertRecipe.fulfilled, (state, action) => {
      if (action.payload?.entities?.dependencies) {
        dependenciesAdapter.upsertMany(state, action.payload.entities.dependencies);
      }
    });

    builder.addCase(updateTasks.fulfilled, (state, action) => {
      if (action.payload?.entities?.dependencies) {
        dependenciesAdapter.updateMany(state, Object.values(action.payload.entities.dependencies));
      }
    });

    builder.addCase(updateDependencies.fulfilled, (state, action) => {
      if (action?.payload?.data) {
        dependenciesAdapter.setMany(state, action.payload.data);
      }
    });

    builder.addCase(bulkDeleteDependency.fulfilled, (state, action) => {
      if (action.payload?.ids) {
        dependenciesAdapter.removeMany(state, action.payload.ids);
      }
    });

    builder.addCase(uploadTasks.fulfilled, (state, action) => {
      if (action.payload?.entities?.dependencies) {
        dependenciesAdapter.upsertMany(state, action.payload.entities.dependencies);
      }
    });

    builder.addCase(bulkUploadDependency.fulfilled, (state, action) => {
      if (action.payload) {
        dependenciesAdapter.upsertMany(state, Object.values(action.payload));
      }
    });

    builder.addCase(fetchChildrenTasks.fulfilled, (state, action) => {
      if (action.payload?.entities?.dependencies) {
        dependenciesAdapter.upsertMany(state, Object.values(action.payload.entities.dependencies));
      }
    });

    // Fill task with FlowAI
    builder.addCase(addFlowAITasks.fulfilled, (state, action) => {
      if (action.payload?.entities?.dependencies) {
        dependenciesAdapter.upsertMany(state, action.payload.entities.dependencies);
      }
    });

    builder.addCase(splitLocationsIntoTasks.fulfilled, (state, action) => {
      if (action.payload?.entities?.dependencies) {
        dependenciesAdapter.upsertMany(state, action.payload?.entities?.dependencies);
      }
    });
  },
});

export default dependenciesSlice.reducer;

/** Selectors **/

export const {
  selectById: selectDependencyById,
  selectIds: selectDependencyIds,
  selectEntities: selectDependencyEntities,
  selectAll: selectAllDependencies,
  selectTotal: selectTotalDependencies,
} = dependenciesAdapter.getSelectors((state) => state.dependencies);

export const { saveDependency, deleteDependency, clearDependencies, updateDependencyId } =
  dependenciesSlice.actions;
